import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Edit2, Save, X } from 'lucide-react';
import { FormSection, Textarea, Button } from '../../common/ui';

const RepairInformation = forwardRef(({ 
  repairData, 
  readOnly = true, 
  onSave,
  externalEditMode
}, ref) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({
    acceptance_notes: repairData.acceptance_notes || '',
    warranty_repair: repairData.warranty_repair || false,
    temporary_import: repairData.temporary_import || false,
    report_to_team_required: repairData.report_to_team_required || false,
    shipped_items: repairData.shipped_items || [],
    shipped_items_other: repairData.shipped_items_other || ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState('');

  const effectiveEditing = externalEditMode === 'edit' || isEditing;

  // Sync local editing state with external edit mode
  useEffect(() => {
    if (externalEditMode === 'edit' && !isEditing) {
      setEditedData({
        acceptance_notes: repairData.acceptance_notes || '',
        warranty_repair: repairData.warranty_repair || false,
        temporary_import: repairData.temporary_import || false,
        report_to_team_required: repairData.report_to_team_required || false,
        shipped_items: repairData.shipped_items || [],
        shipped_items_other: repairData.shipped_items_other || ''
      });
      setIsEditing(true);
    } else if (externalEditMode !== 'edit' && isEditing) {
      setIsEditing(false);
    }
  }, [externalEditMode]);

  useImperativeHandle(ref, () => ({
    getEditedData: () => editedData,
    hasChanges: () => {
      const orig = {
        acceptance_notes: (repairData.acceptance_notes || '').trim(),
        warranty_repair: repairData.warranty_repair || false,
        temporary_import: repairData.temporary_import || false,
        report_to_team_required: repairData.report_to_team_required || false,
        shipped_items: JSON.stringify([...(repairData.shipped_items || [])].sort()),
        shipped_items_other: (repairData.shipped_items_other || '').trim()
      };
      const curr = {
        acceptance_notes: (editedData.acceptance_notes || '').trim(),
        warranty_repair: editedData.warranty_repair || false,
        temporary_import: editedData.temporary_import || false,
        report_to_team_required: editedData.report_to_team_required || false,
        shipped_items: JSON.stringify([...(editedData.shipped_items || [])].sort()),
        shipped_items_other: (editedData.shipped_items_other || '').trim()
      };
      return JSON.stringify(orig) !== JSON.stringify(curr);
    },
    validate: () => {
      if (!editedData.acceptance_notes || !editedData.acceptance_notes.trim()) {
        return { valid: false, error: 'Acceptance notes are required' };
      }
      if ((editedData.shipped_items || []).includes('Other') && (!editedData.shipped_items_other || editedData.shipped_items_other.trim() === '')) {
        return { valid: false, error: 'Please specify what other items were shipped when "Other" is checked' };
      }
      return { valid: true, error: null };
    },
    reset: () => {
      setEditedData({
        acceptance_notes: repairData.acceptance_notes || '',
        warranty_repair: repairData.warranty_repair || false,
        temporary_import: repairData.temporary_import || false,
        report_to_team_required: repairData.report_to_team_required || false,
        shipped_items: repairData.shipped_items || [],
        shipped_items_other: repairData.shipped_items_other || ''
      });
      setValidationError('');
    }
  }));

  // Update internal state when repairData prop changes (e.g., after save/refresh)
  useEffect(() => {
    if (!effectiveEditing) {
      setEditedData({
        acceptance_notes: repairData.acceptance_notes || '',
        warranty_repair: repairData.warranty_repair || false,
        temporary_import: repairData.temporary_import || false,
        report_to_team_required: repairData.report_to_team_required || false,
        shipped_items: repairData.shipped_items || [],
        shipped_items_other: repairData.shipped_items_other || ''
      });
    }
  }, [repairData.acceptance_notes, repairData.warranty_repair, repairData.temporary_import, repairData.report_to_team_required, repairData.shipped_items, repairData.shipped_items_other, effectiveEditing]);

  const handleEdit = () => {
    setEditedData({
      acceptance_notes: repairData.acceptance_notes || '',
      warranty_repair: repairData.warranty_repair || false,
      temporary_import: repairData.temporary_import || false,
      report_to_team_required: repairData.report_to_team_required || false,
      shipped_items: repairData.shipped_items || [],
      shipped_items_other: repairData.shipped_items_other || ''
    });
    setValidationError('');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedData({
      acceptance_notes: repairData.acceptance_notes || '',
      warranty_repair: repairData.warranty_repair || false,
      temporary_import: repairData.temporary_import || false,
      report_to_team_required: repairData.report_to_team_required || false,
      shipped_items: repairData.shipped_items || [],
      shipped_items_other: repairData.shipped_items_other || ''
    });
    setValidationError('');
    setIsEditing(false);
  };

  const handleSave = async () => {
    // Validation
    if (!editedData.acceptance_notes || !editedData.acceptance_notes.trim()) {
      setValidationError('Acceptance notes are required');
      return;
    }
    
    // Validate that if "Other" is checked, shipped_items_other must be provided
    if ((editedData.shipped_items || []).includes('Other') && (!editedData.shipped_items_other || editedData.shipped_items_other.trim() === '')) {
      setValidationError('Please specify what other items were shipped when "Other" is checked');
      return;
    }

    if (onSave) {
      setIsSaving(true);
      setValidationError('');
      try {
        await onSave(editedData);
        setIsEditing(false);
      } catch (error) {
        console.error('Error saving repair information:', error);
        setValidationError('Failed to save changes');
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError('');
    }
  };

  const handleCheckboxChange = (field, checked) => {
    setEditedData(prev => ({
      ...prev,
      [field]: checked
    }));
  };

  const handleShippedItemsChange = (item, checked) => {
    setEditedData(prev => {
      const newShippedItems = checked
        ? [...(prev.shipped_items || []), item]
        : (prev.shipped_items || []).filter(i => i !== item);
      
      // If "Other" checkbox is unchecked, clear the shipped_items_other field
      const shouldClearOther = item === 'Other' && !checked;
      
      return {
        ...prev,
        shipped_items: newShippedItems,
        shipped_items_other: shouldClearOther ? '' : prev.shipped_items_other
      };
    });
  };

  const renderActions = () => {
    if (readOnly || externalEditMode) return null;

    if (isEditing) {
      return (
        <div className="flex gap-2">
          <Button
            onClick={handleSave}
            variant="primary"
            size="xs"
            icon={Save}
            loading={isSaving}
          >
            Save
          </Button>
          <Button
            onClick={handleCancel}
            variant="ghost"
            size="xs"
            icon={X}
            disabled={isSaving}
          >
            Cancel
          </Button>
        </div>
      );
    }

    return (
      <button
        onClick={handleEdit}
        className="text-blue-600 hover:text-blue-700 p-1 rounded hover:bg-blue-50 transition-colors"
        title="Edit repair information"
      >
        <Edit2 className="h-4 w-4" />
      </button>
    );
  };

  return (
    <FormSection title="Repair Information" actions={renderActions()}>
      {validationError && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-800">{validationError}</p>
        </div>
      )}
      
      <div className="space-y-1.5">
        {effectiveEditing ? (
          <>
            {/* Edit Mode - Compact */}
            <div className="bg-gray-50/70 rounded p-2 space-y-1.5">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">
                  Acceptance notes <span className="text-red-500">*</span>
                </label>
                <Textarea
                  size="sm"
                  value={editedData.acceptance_notes}
                  onChange={(e) => handleChange('acceptance_notes', e.target.value)}
                  placeholder="Enter acceptance notes"
                  rows={6}
                  required
                />
              </div>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="flex items-center space-x-1.5">
                  <input
                    type="checkbox"
                    id="edit_warranty_repair"
                    checked={editedData.warranty_repair}
                    onChange={(e) => handleCheckboxChange('warranty_repair', e.target.checked)}
                    className="h-3.5 w-3.5 text-naviter-blue focus:ring-naviter-blue border-gray-300 rounded"
                  />
                  <label htmlFor="edit_warranty_repair" className="text-xs font-medium text-gray-700 whitespace-nowrap">
                    Warranty repair
                  </label>
                </div>

                <div className="flex items-center space-x-1.5">
                  <input
                    type="checkbox"
                    id="edit_temporary_import"
                    checked={editedData.temporary_import}
                    onChange={(e) => handleCheckboxChange('temporary_import', e.target.checked)}
                    className="h-3.5 w-3.5 text-naviter-blue focus:ring-naviter-blue border-gray-300 rounded"
                  />
                  <label htmlFor="edit_temporary_import" className="text-xs font-medium text-gray-700 whitespace-nowrap">
                    Temporary import
                  </label>
                </div>

                <div className="flex items-center space-x-1.5">
                  <input
                    type="checkbox"
                    id="edit_report_to_team_required"
                    checked={editedData.report_to_team_required}
                    onChange={(e) => handleCheckboxChange('report_to_team_required', e.target.checked)}
                    className="h-3.5 w-3.5 text-naviter-blue focus:ring-naviter-blue border-gray-300 rounded"
                  />
                  <label
                    htmlFor="edit_report_to_team_required"
                    className="text-xs font-medium text-gray-700 whitespace-nowrap"
                    title="Cannot be moved to Repaired or later until an internal note has been reported to the team"
                  >
                    Report to the team
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">Items shipped with device</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1">
                  {['Antenna', 'Box', 'Carry bag', 'Jacket', 'SD Card', 'SIM card', 'USB Cable', 'Wall Charger', 'Other'].map(item => (
                    <div key={item} className="flex items-center space-x-1.5 whitespace-nowrap">
                      <input
                        type="checkbox"
                        id={`edit_shipped_item_${item}`}
                        checked={(editedData.shipped_items || []).includes(item)}
                        onChange={(e) => handleShippedItemsChange(item, e.target.checked)}
                        className="h-3.5 w-3.5 flex-shrink-0 text-naviter-blue focus:ring-naviter-blue border-gray-300 rounded"
                      />
                      <label htmlFor={`edit_shipped_item_${item}`} className="text-xs text-gray-700">
                        {item}
                      </label>
                    </div>
                  ))}
                </div>
                {(editedData.shipped_items || []).includes('Other') && (
                  <div className="mt-1.5">
                    <Textarea
                      size="sm"
                      id="edit_shipped_items_other"
                      value={editedData.shipped_items_other || ''}
                      onChange={(e) => handleChange('shipped_items_other', e.target.value)}
                      placeholder="Specify other items..."
                      rows={2}
                      required
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Read-Only Mode - Compact */}
            {repairData.acceptance_notes ? (
              <div className="border-l-2 border-gray-200 pl-3">
                <span className="text-xs uppercase tracking-wide font-medium text-gray-500">Acceptance notes</span>
                <p className="text-sm text-gray-900 whitespace-pre-line mt-0.5">{repairData.acceptance_notes}</p>
              </div>
            ) : (
              <div className="text-gray-400 text-sm italic">No acceptance notes provided</div>
            )}
            <div className="space-y-1.5 mt-2">
              {(repairData.warranty_repair || repairData.temporary_import || repairData.report_to_team_required) && (
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase tracking-wide font-medium text-gray-500">Type</span>
                  <div className="flex gap-2">
                    {repairData.warranty_repair && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-xs">
                        Warranty repair
                      </span>
                    )}
                    {repairData.temporary_import && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-xs">
                        Temporary import
                      </span>
                    )}
                    {repairData.report_to_team_required && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-green-100 text-green-800 text-xs">
                        Report to the team
                      </span>
                    )}
                  </div>
                </div>
              )}
              {repairData.shipped_items && repairData.shipped_items.length > 0 && (
                <div className="flex items-baseline gap-2">
                  <span className="text-xs uppercase tracking-wide font-medium text-gray-500">Items shipped with device</span>
                  <span className="text-sm text-gray-900">
                    {repairData.shipped_items.join(', ')}
                    {repairData.shipped_items.includes('Other') && repairData.shipped_items_other && (
                      <span className="text-gray-600 italic ml-1">({repairData.shipped_items_other})</span>
                    )}
                  </span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </FormSection>
  );
});

RepairInformation.displayName = 'RepairInformation';

export default RepairInformation;

